import React, { useState, useCallback, useEffect } from 'react';
import { X } from 'lucide-react';
import { fetchWithAuth } from '../../lib/api/fetchWithAuth';
import { useAuth0 } from '@auth0/auth0-react';
import { debounce } from 'lodash';

interface CreateBrandModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateBrand: (brandData: { name: string; description?: string; subdomain: string }) => Promise<void>;
}

type SubdomainStatus = 'checking' | 'available' | 'taken' | 'invalid' | null;

export function CreateBrandModal({ isOpen, onClose, onCreateBrand }: CreateBrandModalProps) {
  const { getAccessTokenSilently } = useAuth0();
  const [brandName, setBrandName] = useState('');
  const [brandDescription, setBrandDescription] = useState('');
  const [brandSubdomain, setBrandSubdomain] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [subdomainStatus, setSubdomainStatus] = useState<SubdomainStatus>(null);

  // Debounced subdomain check
  const checkSubdomainAvailability = useCallback(
    debounce(async (subdomain: string) => {
      if (subdomain.length < 3) {
        setSubdomainStatus('invalid');
        return;
      }

      try {
        setSubdomainStatus('checking');
        const token = await getAccessTokenSilently();
        const response = await fetchWithAuth<{ available: boolean }>(`/api/organizations/temp/brands/check-subdomain/${subdomain}`, {
          method: 'GET',
          token
        });
        setSubdomainStatus(response?.available ? 'available' : 'taken');
      } catch (err) {
        console.error('Error checking subdomain:', err);
        setSubdomainStatus(null);
      }
    }, 500),
    [getAccessTokenSilently]
  );

  // Check subdomain availability when it changes
  useEffect(() => {
    if (brandSubdomain) {
      if (brandSubdomain.length < 3) {
        setSubdomainStatus('invalid');
      } else {
        checkSubdomainAvailability(brandSubdomain);
      }
    } else {
      setSubdomainStatus(null);
    }

    // Cleanup
    return () => {
      checkSubdomainAvailability.cancel();
    };
  }, [brandSubdomain, checkSubdomainAvailability]);

  const getSubdomainStatusColor = () => {
    switch (subdomainStatus) {
      case 'available':
        return 'text-green-400';
      case 'taken':
        return 'text-red-400';
      case 'checking':
        return 'text-yellow-400';
      case 'invalid':
        return 'text-red-400';
      default:
        return 'text-gray-400';
    }
  };

  const getSubdomainStatusText = () => {
    switch (subdomainStatus) {
      case 'available':
        return 'Available!';
      case 'taken':
        return 'Already taken';
      case 'checking':
        return 'Checking availability...';
      case 'invalid':
        return 'Must be at least 3 characters';
      default:
        return '';
    }
  };

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!brandName.trim()) return;

    if (subdomainStatus !== 'available') {
      setError('Please choose an available subdomain');
      return;
    }

    try {
      setIsSubmitting(true);
      await onCreateBrand({ 
        name: brandName,
        description: brandDescription,
        subdomain: brandSubdomain
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md relative text-gray-100">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-300"
        >
          <X className="w-5 h-5" />
        </button>

        <h2 className="text-xl font-semibold mb-4 text-gray-100">Create New Brand</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="brandName" className="block text-sm font-medium mb-2 text-gray-300">
              Brand Name
            </label>
            <input
              type="text"
              id="brandName"
              value={brandName}
              onChange={(e) => setBrandName(e.target.value)}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Your Brand"
              required
            />
          </div>

          <div>
            <label htmlFor="brandDescription" className="block text-sm font-medium mb-2 text-gray-300">
              Description
            </label>
            <textarea
              id="brandDescription"
              value={brandDescription}
              onChange={(e) => setBrandDescription(e.target.value)}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Describe your brand"
              rows={3}
            />
          </div>

          <div>
            <label htmlFor="brandSubdomain" className="block text-sm font-medium mb-2 text-gray-300">
              Subdomain
            </label>
            <div className="flex rounded-md shadow-sm">
              <input
                type="text"
                id="brandSubdomain"
                required
                value={brandSubdomain}
                onChange={(e) => setBrandSubdomain(e.target.value)}
                className="flex-1 min-w-0 block w-full px-3 py-2 rounded-l-md bg-gray-700 border border-r-0 border-gray-600 text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="yourbrand"
              />
              <span className="inline-flex items-center px-3 rounded-r-md border border-l-0 border-gray-600 bg-gray-800 text-gray-400">
                .creatorform.app
              </span>
            </div>
            {brandSubdomain.length > 0 && (
              <p className={`mt-1 text-sm ${getSubdomainStatusColor()}`}>
                {getSubdomainStatusText()}
              </p>
            )}
          </div>

          {error && (
            <div className="text-red-400 text-sm">
              {error}
            </div>
          )}

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-300 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !brandName.trim() || !brandSubdomain.trim()}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? 'Creating...' : 'Create Brand'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
} 